import { Injectable, signal } from '@angular/core';

const SESSION_STORAGE_KEY = 'subscriptionBlock';

interface PersistedState {
    blocked: boolean;
    organizationId: string | null;
}

/**
 * Sinal global que indica se a organização atual está com a subscrição
 * inativa (HTTP 402 retornado pelo backend com code
 * `ORGANIZATION_SUBSCRIPTION_INACTIVE`).
 *
 * Persistido em `sessionStorage` para sobreviver a F5 sem causar flash
 * de `/home` antes do interceptor capturar o próximo 402.
 *
 * Limpado automaticamente ao fechar a aba (sessionStorage).
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionBlockState {
    /** True se a request mais recente retornou 402 ORGANIZATION_SUBSCRIPTION_INACTIVE. */
    readonly blocked = signal(false);

    /** ID da organização bloqueada. Pode ser null se o backend não conseguir resolver. */
    readonly organizationId = signal<string | null>(null);

    constructor() {
        this.restore();
    }

    /**
     * Marca o estado como bloqueado. Idempotente — chamadas repetidas
     * com a mesma organização não disparam efeitos colaterais
     * adicionais (mitiga races com múltiplos 402 paralelos).
     */
    block(organizationId: string | null): void {
        if (
            this.blocked() &&
            this.organizationId() === organizationId
        ) {
            return;
        }
        this.organizationId.set(organizationId);
        this.blocked.set(true);
        this.persist();
    }

    clear(): void {
        if (!this.blocked() && this.organizationId() === null) return;
        this.blocked.set(false);
        this.organizationId.set(null);
        this.persist();
    }

    private persist(): void {
        try {
            const state: PersistedState = {
                blocked: this.blocked(),
                organizationId: this.organizationId(),
            };
            if (state.blocked) {
                sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
            } else {
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
            }
        } catch {
            // sessionStorage indisponível (modo privado, etc) — silencioso.
        }
    }

    private restore(): void {
        try {
            const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw) as PersistedState;
            if (parsed?.blocked) {
                this.blocked.set(true);
                this.organizationId.set(parsed.organizationId ?? null);
            }
        } catch {
            // estado inválido — ignora.
        }
    }
}
